import dayjs from 'dayjs'
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { dateFormat } from '@/utils/date'
import { LICENSE_TYPES } from '@/utils/license-types'
import { PLUGIN_OPTIONS_KEYS } from '@root/template-options-keys'

import { handleError } from './error-handling'

export const YEAR = 'year'
export const MONTH = 'month'

export const BILLING_REDIRECT_ID = {
  HOME: 3
}

const {
  WEB_APP_TRIAL,
  WEB_APP_OVERLIMIT,
  WEB_APP_TO_BE_CANCELED,
  WEB_APP_CANCELED,
  WEB_APP_BLOCKED,
  WEB_APP_PAST_DUE,
  WEB_APP_ACTIVE,
  WEB_APP_NONE
} = LICENSE_TYPES
export const ALL_SUBSCRIPTION_STATUSES = [
  {
    label: 'TRIALING',
    value: WEB_APP_TRIAL,
    color: 'var(--grade-medium-color-next)',
    backgroundColor: 'rgba(var(--grade-medium-color-rgb-next), 0.2)'
  },
  {
    label: 'OVERLIMIT',
    value: WEB_APP_OVERLIMIT,
    color: 'var(--grade-medium-color-next)',
    backgroundColor: 'rgba(var(--grade-medium-color-rgb-next), 0.2)'
  },
  // {
  //   label: 'INCOMPLETE EXPIRED',
  //   value: 'incomplete_expired',
  //   color: 'var(--grade-low-color-next)',
  //   backgroundColor: 'rgba(var(--grade-low-color-rgb-next), 0.2)'
  // },
  {
    label: 'TO BE CANCELLED',
    value: WEB_APP_TO_BE_CANCELED,
    color: 'var(--grade-medium-color-next)',
    backgroundColor: 'rgba(var(--grade-medium-color-rgb-next), 0.2)'
  },
  {
    label: 'CANCELED',
    value: WEB_APP_CANCELED,
    color: 'var(--grey-2-next)',
    backgroundColor: 'var(--dark-2)'
  },
  {
    label: 'BLOCKED',
    value: WEB_APP_BLOCKED,
    color: 'var(--grey-2-next)',
    backgroundColor: 'var(--dark-2)'
  },
  // {
  //   label: 'incomplete',
  //   value: 'incomplete',
  //   color: 'var(--grade-medium-color-next)',
  //   backgroundColor: 'rgba(var(--grade-medium-color-rgb-next), 0.2)'
  // },
  {
    label: 'PAST DUE',
    value: WEB_APP_PAST_DUE,
    color: 'var(--grade-medium-color-next)',
    backgroundColor: 'rgba(var(--grade-medium-color-rgb-next), 0.2)'
  },
  // {
  //   label: 'UNPAID',
  //   value: 'unpaid',
  //   color: 'var(--grade-low-color-next)',
  //   backgroundColor: 'rgba(var(--grade-low-color-rgb-next), 0.2)'
  // },
  {
    label: 'ACTIVE',
    value: WEB_APP_ACTIVE,
    color: 'var(--grade-high-color-next)',
    backgroundColor: 'rgba(var(--grade-high-color-rgb-next), 0.2)'
  },
  {
    label: 'NONE',
    value: WEB_APP_NONE,
    color: 'var(--grey-2-next)',
    backgroundColor: 'var(--dark-2)'
  }
]

export const ALL_INVOICES_STATUSES = [
  {
    label: 'Uncollectible',
    value: 'uncollectible',
    color: 'var(--grade-low-color-next)',
    backgroundColor: 'rgba(var(--grade-low-color-rgb-next), 0.2)'
  },
  {
    label: 'Draft',
    value: 'draft',
    color: 'var(--grey-semi-medium)',
    backgroundColor: 'rgba(var(--grey-semi-medium-rgb), 0.2)'
  },
  {
    label: 'Open',
    value: 'open',
    color: 'var(--primary-color-next)',
    backgroundColor: 'rgba(var(--primary-color-rgb-next), 0.2)'
  },
  {
    label: 'Paid',
    value: 'paid',
    color: 'var(--grade-high-color-next)',
    backgroundColor: 'rgba(var(--grade-high-color-rgb-next), 0.2)'
  },
  {
    label: 'Void',
    value: 'void',
    color: 'var(--grade-low-color-next)',
    backgroundColor: 'rgba(var(--grade-low-color-rgb-next), 0.2)'
  }
]

export const ESSENTIAL = 'Essential'
export const PROFESSIONAL = 'Professional'
export const ENTERPRISE = 'Enterprise'

export const isEnterprisePlan = (planName = '') => {
  const normalizedPlanName = (planName ?? '').toString().toLowerCase()
  return normalizedPlanName.includes(ENTERPRISE.toLowerCase())
}

const ESSENTIAL_PLAN_COMMONS = {
  plan: ESSENTIAL,
  isPopular: false,
  isEnterprise: false,
  name: 'Essential',
  subTitle: 'Everything one team needs to set goals and track progress.',
  description: 'per user',
  description2: 'per month',
  features: ['OKR & KPI management', 'Weekly check-ins', 'Roadmap & reporting', 'Jira integration']
}

export const ESSENTIAL_MONTHLY = {
  ...ESSENTIAL_PLAN_COMMONS,
  lookup_key: 'essential_month_v1',
  period: MONTH,
  title: 'Monthly',
  price: 7.5,
  billedPeriod: 'Billed monthly',
  total: 'Total $37.5 / month',
  discount: 0,
  priceWithSign: '$7.5'
}

export const ESSENTIAL_ANNUALLY = {
  ...ESSENTIAL_PLAN_COMMONS,
  lookup_key: 'essential_year_v1',
  period: YEAR,
  title: 'Annually',
  price: 6,
  total: 'Total $30 / month',
  billedPeriod: 'Billed annually',
  discount: 18,
  isAnnual: true,
  priceWithSign: '$6'
}

const PROFESSIONAL_PLAN_COMMONS = {
  plan: PROFESSIONAL,
  isPopular: true,
  isEnterprise: false,
  name: 'Professional',
  subTitle: 'For several teams that need to stay aligned.',
  description: 'per user',
  description2: 'per month',
  features: [
    'Multi-team workspaces',
    'Weighted goals',
    'Deeper OKR hierarchy',
    'Custom reporting & fields'
  ]
}

export const PROFESSIONAL_MONTHLY = {
  ...PROFESSIONAL_PLAN_COMMONS,
  lookup_key: 'professional_month_v1',
  period: MONTH,
  title: 'Monthly',
  price: 12,
  billedPeriod: 'Billed monthly',
  total: 'Total $60 / month',
  discount: 0,
  priceWithSign: '$12'
}

export const PROFESSIONAL_ANNUALLY = {
  ...PROFESSIONAL_PLAN_COMMONS,
  lookup_key: 'professional_year_v1',
  period: YEAR,
  title: 'Annually',
  price: 10,
  total: 'Total $50 / month',
  billedPeriod: 'Billed annually',
  discount: 18,
  isAnnual: true,
  priceWithSign: '$10'
}

export const SUBSCRIPTION_PLANS = [
  {
    name: 'Essential',
    value: ESSENTIAL,
    cycles: [ESSENTIAL_MONTHLY, ESSENTIAL_ANNUALLY]
  },
  {
    name: 'Professional',
    value: PROFESSIONAL,
    cycles: [PROFESSIONAL_MONTHLY, PROFESSIONAL_ANNUALLY]
  }
]

const USDollar = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
})

export const formatAmount = amount => USDollar.format(amount)

export const getAmount = (amount = 0) => {
  return formatAmount(amount / 100)
}

export const getDateFormatted = (date, format = dateFormat) => {
  return dayjs(date * 1000).format(format)
}

const ENTERPRISE_PLAN = {
  isPopular: false,
  isEnterprise: true,
  name: 'Enterprise',
  subTitle: 'Advanced controls and support for your entire organization.',
  priceWithSign: 'Let’s talk',
  subTitle2: 'Contact us to discuss',
  features: [
    'Custom rights & private OKRs',
    'Unlimited scale',
    'Priority support',
    'Implementation consulting'
  ]
}

export const SUBSCRIPTION_TIERS = [
  {
    period: MONTH,
    tiers: [ESSENTIAL_MONTHLY, PROFESSIONAL_MONTHLY, ENTERPRISE_PLAN]
  },
  {
    period: YEAR,
    tiers: [ESSENTIAL_ANNUALLY, PROFESSIONAL_ANNUALLY, ENTERPRISE_PLAN]
  }
]
//
// export const CVC_CHECK_STATUS = {
//   PASS: 'pass',
//   FAIL: 'fail',
//   UNCHECKED: 'unchecked',
//   UNAVAILABLE: 'unavailable'
// }
export const useManagePlanDialog = ({
  subscribeTrackCallback = () => {},
  contactUsTrackCallback = () => {}
}) => {
  const managePlanPayload = ref({})
  const isShowManagePlan = ref(false)

  const onSubscribe = payload => {
    isShowManagePlan.value = true
    managePlanPayload.value = payload

    subscribeTrackCallback?.(payload)
  }

  const isShowBillingContactDialog = ref(false)

  const onContactUs = payload => {
    isShowBillingContactDialog.value = true
    contactUsTrackCallback?.(payload)
  }

  return {
    onSubscribe,
    onContactUs,
    isShowBillingContactDialog,
    isShowManagePlan,
    managePlanPayload
  }
}

const ANNUALLY = 'annually'
const MONTHLY = 'monthly'

export const getPeriodName = period => (period === YEAR ? ANNUALLY : MONTHLY)

export const useSubscription = ({ trackCallback = () => {} } = {}) => {
  const store = useStore()

  const subscription = computed(
    () =>
      store.state.system.billingPlanDetails.data || {
        upcomingInvoice: {}
      }
  )

  const loading = computed(() => store.state.system.billingPlanDetails.loading)

  const statusData = computed(
    () =>
      ALL_SUBSCRIPTION_STATUSES.find(
        status => status.value === subscription.value.statusId?.toString()
      ) || ALL_SUBSCRIPTION_STATUSES[6]
  )

  const licenseStatusId = computed(
    () => store.state.pluginOptions[PLUGIN_OPTIONS_KEYS.LICENSE_STATUS_ID]
  )

  onMounted(async () => {
    try {
      await store.dispatch('system/fetchBillingPlanDetails')
    } catch (error) {
      handleError({ error })
    }

    trackCallback?.({
      period: subscription.value.period
    })
  })

  return {
    loading,
    subscription,
    statusData,
    licenseStatusId
  }
}
